/**
 * SEOHead.jsx - SEO meta tags management component
 * 
 * This component manages:
 * - Page title
 * - Meta description
 * - Canonical URL
 * - Open Graph tags
 * 
 * Usage:
 * <SEOHead 
 *   title="Page Title"
 *   description="Page description"
 *   canonical="/page-path"
 * />
 * 
 * @author Curis Path Development Team
 */

import { useEffect } from 'react';

// Base URL for the production website
const BASE_URL = 'https://curispath.com';

const SEOHead = ({ 
  title, 
  description, 
  canonical,
  ogImage = '/og-image.jpg'
}) => {
  useEffect(() => {
    // Update page title
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (selector, attribute, value) => {
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, value);
      } else {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        element.setAttribute(attribute, value);
        document.head.appendChild(element);
      }
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (element) {
        element.setAttribute('href', href);
      } else {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        element.setAttribute('href', href);
        document.head.appendChild(element);
      }
    };

    // Update meta description
    if (description) {
      updateMetaTag('meta[name="description"]', 'content', description);
    }

    // Update canonical URL - CRITICAL FOR SEO
    if (canonical) {
      const fullCanonicalUrl = canonical.startsWith('http') 
        ? canonical 
        : `${BASE_URL}${canonical}`;
      updateLinkTag('canonical', fullCanonicalUrl);
    }

    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', 'content', title);
    if (description) {
      updateMetaTag('meta[property="og:description"]', 'content', description);
    }
    if (canonical) {
      const fullUrl = canonical.startsWith('http') ? canonical : `${BASE_URL}${canonical}`;
      updateMetaTag('meta[property="og:url"]', 'content', fullUrl);
    }

    // Update Twitter Card tags
    updateMetaTag('meta[name="twitter:title"]', 'content', title);
    if (description) {
      updateMetaTag('meta[name="twitter:description"]', 'content', description);
    }

  }, [title, description, canonical, ogImage]);

  return null; // This component doesn't render anything
};

export default SEOHead;
